import { Box, Button, Container, Grid, Typography } from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import { Link as RouterLink } from 'react-router-dom'
import { useEffect, useState } from 'react'
import promoVideo1 from '@/assets/promo1.mp4'
import promoVideo2 from '@/assets/promo2.mp4'
import promoVideo3 from '@/assets/promo3.mp4'

const MotionBox = motion(Box)

const AnimatedPlaceholder = () => {
  const [iteration, setIteration] = useState(0)
  
  useEffect(() => {
    const timer = setInterval(() => {
      setIteration(prev => (prev + 1) % 3)
    }, 2000)
    return () => clearInterval(timer)
  }, [])

  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8 }}
      sx={{
        height: '100%',
        minHeight: 400,
        backgroundColor: 'grey.100',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <AnimatePresence mode="wait">
        <MotionBox
          key={iteration}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
          sx={{
            position: 'absolute',
            width: '100%',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          {iteration === 0 && (
            <>
              <Typography variant="h4" color="primary">
                Generativní AI Design
              </Typography>
              <Box
                component="video"
                autoPlay
                muted
                loop
                playsInline
                sx={{
                  width: '100%',
                  maxWidth: '500px',
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: 3
                }}
              >
                <source src={promoVideo1} type="video/mp4" />
                Your browser does not support the video tag.
              </Box>
            </>
          )}
          {iteration === 1 && (
            <>
              <Typography variant="h4" color="primary">
                Optimalizace Výroby
              </Typography>
              <Box
                component="video"
                autoPlay
                muted
                loop
                playsInline
                sx={{
                  width: '100%',
                  maxWidth: '500px',
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: 3
                }}
              >
                <source src={promoVideo2} type="video/mp4" />
                Your browser does not support the video tag.
              </Box>
            </>
          )}
          {iteration === 2 && (
            <>
              <Typography variant="h4" color="primary">
                Systémová Integrace
              </Typography>
              <Box
                component="video"
                autoPlay
                muted
                loop
                playsInline
                sx={{
                  width: '100%',
                  maxWidth: '500px',
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: 3
                }}
              >
                <source src={promoVideo3} type="video/mp4" />
                Your browser does not support the video tag.
              </Box>
            </>
          )}
        </MotionBox>
      </AnimatePresence>
    </MotionBox>
  )
}

export default function Home() {
  return (
    <Container>
      <Grid container spacing={6} sx={{ my: 4 }}>
        <Grid item xs={12} md={6}>
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(45deg, #1976d2 30%, #9c27b0 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              AGENIO
            </Typography>
            <Typography variant="h4" gutterBottom color="text.secondary">
              Advanced GENeration with Intelligent Optimization
            </Typography>
            <Typography variant="body1" paragraph>
              Inovativní softwarová platforma využívající umělou inteligenci pro optimalizaci průmyslového designu a výrobních procesů.
            </Typography>
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              size="large"
              sx={{ mr: 2 }}
            >
              Kontaktujte nás
            </Button>
            <Button
              component={RouterLink}
              to="/features"
              variant="outlined"
              size="large"
            >
              Více informací
            </Button>
          </MotionBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <AnimatedPlaceholder />
        </Grid>
      </Grid>

      <Box sx={{ my: 8 }}>
        <Typography variant="h2" gutterBottom align="center">
          Klíčové funkce
        </Typography>
        <Grid container spacing={4} sx={{ mt: 4 }}>
          {[
            {
              title: 'Generativní AI Design',
              description: 'Automatizované generování návrhů produktů a výrobních procesů s optimalizací pro výrobní omezení.',
            },
            {
              title: 'Optimalizace výroby',
              description: 'Analýza výrobních dat v reálném čase s prediktivní údržbou zařízení.',
            },
            {
              title: 'Systémová integrace',
              description: 'Bezproblémové napojení na existující průmyslové systémy (ERP, MES, PLM).',
            },
          ].map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
              >
                <Typography variant="h5" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {feature.description}
                </Typography>
              </MotionBox>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  )
} 